import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { type CalendarPhase, listCalendarPhases } from "api-client";

import { getCalendarPhaseYear } from "src/calendar/calendarPhaseYear";
import {
  getCalendarCellId,
  getCalendarFocusMonth,
} from "src/calendar/calendarWeeks";
import { assignPhaseLanes } from "src/components/phaseRanges";
import { type CalendarInitialView } from "src/lib/calendarViewRoute";

const CALENDAR_PHASES_STALE_TIME = 5_000;
const CALENDAR_PHASES_CACHE_TIME = 60_000;

export interface CalendarPhaseWithCells extends CalendarPhase {
  start: number;
  end: number;
  lane: number;
}

export const calendarPhaseKeys = {
  athlete: (athleteId: string) => {
    return ["calendar-phases", athleteId] as const;
  },
  year: (athleteId: string, year: number) => {
    return [...calendarPhaseKeys.athlete(athleteId), year] as const;
  },
};

export function toCalendarPhasesWithCells(
  phases: CalendarPhase[],
): CalendarPhaseWithCells[] {
  return assignPhaseLanes(
    phases.map((phase) => {
      return {
        ...phase,
        start: getCalendarCellId(phase.startDate),
        end: getCalendarCellId(phase.endDate) + 1,
      };
    }),
  );
}

export function calendarYearPhasesQueryOptions(
  athleteId: string,
  year: number,
) {
  const phaseYear = getCalendarPhaseYear(year);

  return queryOptions({
    queryKey: calendarPhaseKeys.year(athleteId, year),
    queryFn: () => {
      return listCalendarPhases({
        path: { athleteId },
        query: { startDate: phaseYear.startDate, endDate: phaseYear.lastDate },
      }).then((response) => {
        return response.data.data.phases;
      });
    },
    select: toCalendarPhasesWithCells,
    staleTime: CALENDAR_PHASES_STALE_TIME,
    gcTime: CALENDAR_PHASES_CACHE_TIME,
    refetchOnMount: false,
  });
}

export async function fetchCalendarInitialPhases(
  queryClient: QueryClient,
  athleteId: string,
  calendar: CalendarInitialView,
) {
  if (calendar.view === "month") {
    await queryClient.query(
      calendarYearPhasesQueryOptions(
        athleteId,
        Number(getCalendarFocusMonth(calendar.initialWeekIndex).slice(0, 4)),
      ),
    );
  }
}

export function replaceCachedCalendarPhases(
  queryClient: QueryClient,
  athleteId: string,
  update: { removedPhaseIds: string[]; phases: CalendarPhase[] },
) {
  const replacedPhaseIds = new Set([
    ...update.removedPhaseIds,
    ...update.phases.map((phase) => {
      return phase.id;
    }),
  ]);

  queryClient
    .getQueriesData<CalendarPhase[]>({
      queryKey: calendarPhaseKeys.athlete(athleteId),
    })
    .forEach(([queryKey, phases]) => {
      if (!phases) return;

      const phaseYear = getCalendarPhaseYear(Number(queryKey[2]));
      const yearPhases = update.phases.filter((phase) => {
        return (
          phase.startDate <= phaseYear.lastDate &&
          phase.endDate >= phaseYear.startDate
        );
      });

      queryClient.setQueryData(
        queryKey,
        [
          ...phases.filter((phase) => {
            return !replacedPhaseIds.has(phase.id);
          }),
          ...yearPhases,
        ].sort((a, b) => {
          return a.startDate.localeCompare(b.startDate);
        }),
      );
    });
}
